import React, { useEffect } from 'react';
import { Button, Table, Tag } from 'antd';
import { Link, withRouter } from 'react-router-dom';
import moment from 'moment';
import { withAppContext } from 'context/AppContext';
import { buildEInvoiceCouponCreateUri, buildEInvoiceCouponDetailUri } from 'utils/routes';
import { useGetPaginatedVouchers } from 'utils/apis/eInvoiceCoupon';
import {
  constructColumn,
  constructColumnFilterSearch,
  constructColumnFilterCheckbox,
  constructColumnFilterRadio,
  handleOnAggregationTableChange
} from 'utils/table/table';
import { StyledCard } from './eInvoice.styles';
import { formatAmountOff } from 'utils/currencyDisplay';

const formatDiscount = record => {
  if (record.type === 'percent_off' && record.percentageOff != null) {
    return `${record.percentageOff}% off`;
  }
  if (record.amountOff != null) {
    return formatAmountOff(record.amountOff, record.currency);
  }
  return '—';
};

const formatDuration = record => {
  if (!record.duration) return '—';
  if (record.duration === 'repeating' && record.durationInMonths) {
    return `${record.duration} (${record.durationInMonths} months)`;
  }
  return record.duration;
};

const formatStatus = record => {
  if (record.pause || record.promotionCodeActive === false) {
    return <Tag color="red">Inactive</Tag>;
  }
  return <Tag color="green">Active</Tag>;
};

const DURATION_FILTER_OPTIONS = [
  { label: 'Once', value: 'once' },
  { label: 'Repeating', value: 'repeating' },
  { label: 'Forever', value: 'forever' }
];

const STATUS_FILTER_OPTIONS = [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }];

const constructColumns = ({ filters = {}, sorter = {} }) => [
  {
    ...constructColumn('Code', 'promotionCode'),
    ...constructColumnFilterSearch('promotionCode', 'Search Code', filters),
    render: (text, record) => <Link to={buildEInvoiceCouponDetailUri(String(record._id))}>{text || record.code || '—'}</Link>
  },
  {
    ...constructColumn('Name', 'name', { hasSorter: true, sorter }),
    ...constructColumnFilterSearch('name', 'Search Name', filters)
  },
  {
    title: 'Discount',
    key: 'discount',
    render: (_, record) => formatDiscount(record)
  },
  {
    ...constructColumn('Duration', 'duration'),
    ...constructColumnFilterCheckbox('duration', DURATION_FILTER_OPTIONS),
    onFilter: () => true,
    render: (_, record) => formatDuration(record)
  },
  {
    title: 'Status',
    key: 'status',
    ...constructColumnFilterRadio('status', STATUS_FILTER_OPTIONS),
    onFilter: () => true,
    render: (_, record) => formatStatus(record)
  },
  {
    title: 'Redemptions',
    key: 'redemptions',
    render: (_, record) => {
      const redeemed =
        record.promotionTimesRedeemed != null ? record.promotionTimesRedeemed : record.timesRedeemed != null ? record.timesRedeemed : 0;
      const max = record.promotionMaxRedemptions != null ? record.promotionMaxRedemptions : record.maxRedemptions;
      return max ? `${redeemed} / ${max}` : redeemed;
    }
  },
  {
    ...constructColumn('Expires', 'redeemBy', { hasAggregationSorter: true, sorter }),
    sortOrder: sorter.columnKey === 'redeemBy' ? sorter.order : false,
    render: (_, record) => {
      return record.redeemBy ? moment(record.redeemBy).format('YYYY-MM-DD HH:mm') : '—';
    }
  }
];

const Coupon = props => {
  const { user, history } = props;
  const [query, setQuery] = React.useState({ currentPage: 1, limit: 10, filter: {}, sorter: {} });
  const [tableQuery, setTableQuery] = React.useState({ filters: {}, sorter: {} });
  const { isLoading, paginatedData: voucherList, total } = useGetPaginatedVouchers(query, {
    shouldFetch: Boolean(user && user.isAdmin)
  });

  useEffect(() => {
    if (user && !user.isAdmin) {
      history.push('/forbidden');
    }
  }, [user, history]);

  if (!user || !user.isAdmin) {
    return null;
  }

  return (
    <StyledCard
      title="Coupons"
      extra={
        <Link to={buildEInvoiceCouponCreateUri()}>
          <Button type="primary" icon="plus">
            Create Coupon
          </Button>
        </Link>
      }
    >
      <Table
        loading={isLoading}
        rowKey={record => String(record._id)}
        columns={constructColumns({ filters: tableQuery.filters || {}, sorter: tableQuery.sorter || {} })}
        dataSource={voucherList}
        onChange={(pagination, filters, sorter) => {
          setTableQuery({ filters, sorter });
          handleOnAggregationTableChange({ pagination, filters, sorter }, setQuery);
        }}
        scroll={{ x: true }}
        pagination={{
          total,
          current: query.currentPage,
          pageSize: query.limit,
          showSizeChanger: true
        }}
      />
    </StyledCard>
  );
};

export default withAppContext(withRouter(Coupon));
